import React from "react";
import { Link } from "react-router-dom";
import { handleImageError, pickClusterImage } from "../../utils/imageUtils";

// Trim a summary to a target character count so every card has approximately
// the same amount of text. Lands on a word boundary within ±slack characters
// of the target so words aren't cut in half. Appends an ellipsis when cut.
const trimSummary = (text, target = 280, slack = 5) => {
  if (!text) return "";
  const t = text.trim();
  if (t.length <= target + slack) return t;

  // Look for a space inside [target - slack, target + slack]; prefer the one
  // closest to target so we stay within the ±slack window when possible.
  const min = Math.max(0, target - slack);
  const max = Math.min(t.length, target + slack);
  let cut = -1;
  let bestDist = Infinity;
  for (let i = min; i <= max; i++) {
    if (t[i] === " ") {
      const d = Math.abs(i - target);
      if (d < bestDist) {
        bestDist = d;
        cut = i;
      }
    }
  }

  // Fallback: previous space before target (rarely needed).
  if (cut === -1) cut = t.lastIndexOf(" ", target);
  if (cut === -1) cut = target; // last resort

  return t.slice(0, cut).replace(/[\s,;:.\-–—]+$/, "") + "…";
};

export function SectionHeader({ title, eyebrow, viewAllHref }) {
  return (
    <div className="mb-4 flex items-baseline gap-3 border-b-2 border-ink pb-2.5">
      {eyebrow && (
        <span className="font-head text-[12px] font-bold uppercase tracking-wider text-brand">
          ● {eyebrow}
        </span>
      )}
      <h2 className="font-head text-[22px] font-bold text-ink">{title}</h2>
      {viewAllHref && (
        <Link
          to={viewAllHref}
          className="ml-auto font-head text-[12.5px] font-semibold text-brand hover:underline"
        >
          Të gjitha →
        </Link>
      )}
    </div>
  );
}

/**
 * Single cluster row used inside both SectionBlock (single column) and the
 * paired-grid layout in CategoryList (rows synced across columns).
 *
 * `withTopBorder` controls whether to render the hairline separator above
 * the item — turn off on the first row of a paired grid where the header
 * already provides a divider.
 */
export function SectionItem({
  clusterId,
  clusterData,
  category,
  withTopBorder = true,
}) {
  const article = clusterData?.articles?.[0];
  if (!article) return null;
  const count = clusterData.articles.length;
  const thumb = pickClusterImage(clusterData.articles) || "/fallback.jpg";

  return (
    <div
      className={`min-h-[180px] py-3 ${
        withTopBorder ? "border-t border-line" : ""
      }`}
    >
      <a
        href={article.link}
        target="_blank"
        rel="noopener noreferrer"
        className="float-right ml-3 mb-1 block w-[80px] overflow-hidden rounded-card bg-chip md:w-[92px]"
      >
        <div className="aspect-[4/3]">
          <img
            src={thumb}
            alt=""
            referrerPolicy="no-referrer"
            className="h-full w-full object-cover transition hover:scale-105"
            onError={(e) => handleImageError(e, thumb)}
          />
        </div>
      </a>
      <a
        href={article.link}
        target="_blank"
        rel="noopener noreferrer"
        className="block hover:underline"
      >
        <h4 className="line-clamp-2 font-head text-[13.5px] font-bold leading-snug tracking-tight text-ink md:text-[14px]">
          {article.title}
        </h4>
      </a>
      {article.summary && (
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-sub">
          {trimSummary(article.summary, 150, 5)}
        </p>
      )}
      <div className="clear-both mt-2 flex flex-wrap items-center gap-1.5">
        <span className="font-head text-[10.5px] font-semibold text-brand">
          {article.portal}
        </span>
        <span className="rounded-pill bg-chip px-1.5 py-0.5 text-[10px] text-chip-ink">
          {count} lajme
        </span>
        {count > 3 && (
          <Link
            to={`/category/${category}/cluster/${clusterId}`}
            className="ml-auto font-head text-[11px] font-semibold text-brand hover:underline"
          >
            Të gjitha →
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Opinion ("Kolumna") row — mirrors SectionItem's list layout so a Kolumna
 * column lines up visually next to a category column in the paired grid.
 * Takes an opinion item { link, title, author, portal, summary, imageUrl }.
 */
export function OpinionItem({ item, withTopBorder = true }) {
  if (!item) return null;
  const thumb = item.imageUrl || "/fallback.jpg";

  return (
    <div
      className={`min-h-[180px] py-3 ${
        withTopBorder ? "border-t border-line" : ""
      }`}
    >
      {item.imageUrl && (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="float-right ml-3 mb-1 block w-[80px] overflow-hidden rounded-card bg-chip md:w-[92px]"
        >
          <div className="aspect-[4/3]">
            <img
              src={thumb}
              alt=""
              referrerPolicy="no-referrer"
              className="h-full w-full object-cover transition hover:scale-105"
              onError={(e) => handleImageError(e, thumb)}
            />
          </div>
        </a>
      )}
      {item.author && (
        <span className="mb-0.5 block font-head text-[10.5px] font-bold uppercase tracking-wider text-ai">
          {item.author}
        </span>
      )}
      <a
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        className="block hover:underline"
      >
        <h4 className="line-clamp-2 font-head text-[13.5px] font-bold leading-snug tracking-tight text-ink md:text-[14px]">
          {item.title}
        </h4>
      </a>
      {item.summary && (
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-sub">
          {trimSummary(item.summary, 150, 5)}
        </p>
      )}
      <div className="clear-both mt-2 flex flex-wrap items-center gap-1.5">
        <span className="font-head text-[10.5px] font-semibold text-brand">
          {item.portal}
        </span>
      </div>
    </div>
  );
}

/**
 * Compact list-block for a single category column. Used on the CategoryPage.
 * On the homepage, CategoryList uses SectionItem directly inside a paired
 * grid so rows can sync heights across two columns.
 */
export function SectionBlock({ title, items, category }) {
  return (
    <section>
      <SectionHeader title={title} viewAllHref={`/category/${title}`} />
      {items.length === 0 && (
        <p className="text-[13px] text-sub">Nuk ka lajme në këtë seksion.</p>
      )}
      {items.map(([clusterId, clusterData], idx) => (
        <SectionItem
          key={clusterId}
          clusterId={clusterId}
          clusterData={clusterData}
          category={category || title}
          withTopBorder={idx > 0}
        />
      ))}
    </section>
  );
}

export function MostRead({ items }) {
  return (
    <div className="rounded-card border border-line bg-surface p-5">
      <h3 className="mb-3.5 font-head text-[12px] font-bold uppercase tracking-wider text-sub">
        Më të lexuarat sot
      </h3>
      {items.length === 0 && (
        <p className="text-[12.5px] text-sub">Asgjë për momentin.</p>
      )}
      {items.map((x, i) => (
        <a
          key={`${x.title}-${i}`}
          href={x.link || "#"}
          target={x.link ? "_blank" : undefined}
          rel="noopener noreferrer"
          className="flex gap-3 border-t border-line py-2.5 first:border-t-0 hover:bg-chip/40"
        >
          <span className="w-5 shrink-0 font-head text-[17px] font-bold text-brand">
            {i + 1}
          </span>
          <span className="text-[13px] leading-snug text-ink">{x.title}</span>
        </a>
      ))}
    </div>
  );
}
